import { useRouter } from 'next/router';
import { useCallback, useContext, useState } from 'react';
import { getFeatureTagIdBySlug } from 'src/config/featureTag';
import { QUICK_SHIP_FEATURE_TAG_ID } from 'src/constants/featureTags';
import { useShopSelector } from 'src/hooks/useShopSelector';
import { Type as UseProductsFeatureTagType } from 'src/libs/api/productsApi';
import type { Product, ProductDetail, ProductResponse } from 'src/models/product/schema';
import { FeatureTagsContext } from 'src/stores/FeatureTags';
import { LOCALE_TYPE } from 'src/types/locale';
import type { RankingApiParameter } from 'src/types/product';
import type { SortType } from 'src/types/productSort';
import { BackendApiFetch } from 'src/utils/backendApi';
import { errorMessage } from 'src/utils/errorMessage';
import type { Fetcher, Key } from 'swr';
import useSWR from 'swr';
import useSWRInfinite from 'swr/infinite';
import { fetchProducts, fetchProductsByIds } from '../libs/api/productsApi';

const useProducts = (
  id: number,
  type: UseProductsFeatureTagType,
  page: number,
  productCountPerPage: number,
  shopId?: number,
  additionalParam?: {
    type: string;
    id: number;
  },
  isSort?: boolean,
  allOrder: boolean = true
): {
  products: Product[] | undefined;
  isProductsLoading: boolean;
  isProductsError: boolean;
  totalPageCount: number | undefined;
  sort: SortType;
  setSort: React.Dispatch<React.SetStateAction<SortType>>;
} => {
  // 「今すぐ発送できる商品」用のエリアではquickShipをtrueに設定し、販売されている商品のみを表示する
  const effectiveQuickShip = type === 'featureTagId' && id === QUICK_SHIP_FEATURE_TAG_ID;
  const quickShipParam = effectiveQuickShip ? '&quickShip=true' : '';

  const [sort, setSort] = useState<SortType>(isSort ? 'popular' : '');
  const { locale } = useRouter();

  const getPath = () => {
    const basePath = `/web/v1/products?page=${page}&items=${productCountPerPage}${quickShipParam}`;
    if (id === 0 || type === 'all') return basePath;
    const additionalParams = additionalParam ? `&${additionalParam.type}=${additionalParam.id}` : '';
    const sortParam = sort ? `&sort=${sort}` : '';
    return `${basePath}&${type}=${id}${shopId ? `&shopId=${shopId}` : ''}${additionalParams}${sortParam}`;
  };

  const swrKey = locale && id ? `${getPath()}` : '';

  const fetcher = useCallback(async (): Promise<ProductResponse> => {
    return await fetchProducts(
      id,
      type,
      locale ?? LOCALE_TYPE.EN,
      page,
      productCountPerPage,
      shopId,
      additionalParam,
      allOrder,
      undefined,
      sort
    );
  }, [id, type, locale, page, productCountPerPage, shopId, additionalParam, allOrder, sort]);

  const { data, error, isLoading } = useSWR(swrKey, fetcher, {
    errorRetryCount: 0,
    dedupingInterval: 3600000 // 1 hour interval
  });

  return {
    products: data?.products,
    isProductsLoading: isLoading,
    isProductsError: !!error,
    totalPageCount: data?.totalPageCount,
    sort,
    setSort
  };
};

const useProductsByInfiniteScroll = (
  id: number,
  type: UseProductsFeatureTagType,
  items: number,
  shopId?: number,
  additionalParam?: {
    type: string;
    id: number;
  }
): {
  products: Product[] | undefined;
  isProductsLoading: boolean;
  isProductsError: boolean;
  loadMore: () => void;
  isDisplayLastProduct: boolean;
} => {
  const { locale } = useRouter();
  const fetcher = useCallback(
    async (path: string): Promise<Product[]> => {
      try {
        const res = await BackendApiFetch<Product[]>({
          path: `${path}&allOrder=true`,
          localeType: locale
        });
        return res.data;
      } catch (e) {
        throw new Error(errorMessage(e));
      }
    },
    [locale]
  );
  const getKey = (pageIndex: number) => {
    if (locale && id) {
      if (additionalParam && additionalParam.id !== 0) {
        return `/web/v1/products?shopId=${shopId}&page=${pageIndex + 1}&items=${items}&${type}=${id}&${
          additionalParam.type
        }=${additionalParam.id}`;
      }
      return `/web/v1/products?shopId=${shopId}&page=${pageIndex + 1}&items=${items}&${type}=${id}`;
    }

    return null;
  };

  const { data, error, setSize } = useSWRInfinite(getKey, fetcher, {
    errorRetryCount: 0,
    dedupingInterval: 3600000 // 1時間,
  });
  const products = data ? data.flat() : undefined;
  const isDisplayLastProduct = data ? data[data.length - 1].length < items : false;

  return {
    products: products,
    isProductsLoading: !data && !error,
    isProductsError: !!error,
    loadMore: () => setSize((size) => size + 1),
    isDisplayLastProduct: isDisplayLastProduct
  };
};

const useProductsByIds = (
  ids: number[],
  page: number,
  items: number
): {
  products: Product[] | undefined;
  isProductsLoading: boolean;
  isProductsError: boolean;
} => {
  const { locale } = useRouter();
  const fetcher = useCallback(async (): Promise<Product[]> => {
    return await fetchProductsByIds(ids, page, items, locale ?? 'en');
  }, [ids, page, items, locale]);
  const { data, error } = useSWR(locale ? `/api/products?page=${page}&items=${items}&ids=[${ids}]` : null, fetcher, {
    errorRetryCount: 0,
    dedupingInterval: 3600000 // 1時間,
  });

  return {
    products: data,
    isProductsLoading: !data && !error,
    isProductsError: !!error
  };
};

const useProductsByRanking = (
  params: RankingApiParameter,
  maxProductsCount: number
): {
  products: ProductDetail[] | undefined;
  isProductsLoading: boolean;
  isProductsError: boolean;
} => {
  const { locale } = useRouter();
  const fetcher: Fetcher<ProductDetail[], Key> = useCallback(
    async (path: string): Promise<ProductDetail[]> => {
      try {
        const res = await BackendApiFetch<ProductDetail[]>({
          path: `${path}&inStock=true`,
          localeType: locale
        });
        return res.data;
      } catch (e) {
        throw new Error(errorMessage(e));
      }
    },
    [locale]
  );
  const getKey = () => {
    const categoryTypeSpecified = params.type !== 'all';
    return categoryTypeSpecified
      ? `/api/v1/rankings?type=${params.type}&slug=${params.slug}&number=${maxProductsCount}`
      : `/api/v1/rankings?type=${params.type}&number=${maxProductsCount}`;
  };

  const { data, error, isLoading } = useSWR(getKey(), fetcher, {
    errorRetryCount: 0,
    dedupingInterval: 3600000 // 1時間,
  });

  return {
    products: data,
    isProductsLoading: isLoading,
    isProductsError: !!error
  };
};

const useProductsByFeatureTag = (
  featureTagSlug: string,
  page: number = 1,
  items: number = 6
): {
  products: Product[] | undefined;
  isProductsLoading: boolean;
} => {
  const { currentShop } = useShopSelector();
  const featureTags = useContext(FeatureTagsContext);
  const { products, isProductsLoading } = useProducts(
    getFeatureTagIdBySlug(featureTags, featureTagSlug),
    UseProductsFeatureTagType.FEATURE_TAG_ID,
    page,
    items,
    currentShop?.id
  );
  return {
    products,
    isProductsLoading
  };
};

export { useProducts, useProductsByFeatureTag, useProductsByIds, useProductsByInfiniteScroll, useProductsByRanking };
